import { router } from "@inertiajs/react"
import { Check, Plus } from "lucide-react"
import { useState } from "react"
import { GroupContextTrigger } from "@/components/GroupContextTrigger"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog"
import { cn } from "@/lib/utils"
import { CreateGroupDialog } from "./CreateGroupDialog"

export function CoachGroupSwitcher({ coachGroup, coachGroups }) {
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  // Renders once the coach has at least one group -- a single-group coach
  // still needs the "+ New group" entry point to create an additional one.
  // A groupless coach has no active group to show here; that path is the
  // Dashboard's "Create your first group" empty state instead.
  if (!coachGroups || coachGroups.length < 1) return null

  const handleSwitch = (groupId) => {
    if (groupId === coachGroup?.id) {
      setOpen(false)
      return
    }

    setSwitching(true)
    router.patch(
      "/app/coach/coach_group_switch",
      { group_id: groupId },
      {
        onFinish: () => {
          setSwitching(false)
          setOpen(false)
        },
      }
    )
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <GroupContextTrigger
        groupName={coachGroup?.name}
        busy={switching}
        ariaLabel={`Change coaching group — currently viewing ${coachGroup?.name ?? "a group"}.`}
        onClick={() => setOpen(true)}
      />
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Choose a coach group</ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            This page reloads with the roster and assignments for whichever group you pick.
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <div className="px-4 pb-4 space-y-1">
          {coachGroups.map((group) => (
            <Button
              key={group.id}
              variant="ghost"
              className="w-full justify-start gap-3 h-11"
              onClick={() => handleSwitch(group.id)}
              disabled={switching}
            >
              <Check
                className={cn(
                  "h-4 w-4 shrink-0",
                  group.id === coachGroup?.id ? "opacity-100" : "opacity-0"
                )}
              />
              <span className="truncate">{group.name}</span>
            </Button>
          ))}
          <Separator className="my-1" />
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 h-11 text-kf-red hover:text-kf-red"
            onClick={() => {
              setOpen(false)
              setCreateOpen(true)
            }}
          >
            <Plus className="h-4 w-4 shrink-0" />
            <span>New group</span>
          </Button>
        </div>
      </ResponsiveDialogContent>
      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} />
    </ResponsiveDialog>
  )
}
